import { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { ManagerOverrideModal } from '@/components/common/ManagerOverrideModal.js';
import { useManagerOverride } from '@/hooks/useManagerOverride.js';
import {
  processReturn,
  type ProcessReturnInput,
  type SaleDetail,
  type SaleLineItem,
} from '@/lib/api/sales.js';
import { parseApiError } from '@/lib/api/errors.js';
import { formatCurrency } from '@/lib/format.js';
import {
  paymentMethodEmoji,
  paymentMethodLabel,
} from '@/lib/payment-display.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { cn } from '@/lib/utils.js';

type ReturnItemDraft = {
  lineItemId: string;
  description: string;
  maxQty: number;
  unitPrice: number;
  selected: boolean;
  quantity: number;
  returnToStock: boolean;
  condition: 'good' | 'damaged';
  conditionNotes: string;
};

type RefundMethod = ProcessReturnInput['refundMethod'];

function buildDrafts(lineItems: SaleLineItem[]): ReturnItemDraft[] {
  return lineItems
    .filter((line) => line.type !== 'discount')
    .map((line) => {
      const sold = Number(line.quantity);
      const alreadyReturned = Number(line.returnedQty ?? 0);
      const maxQty = Math.max(sold - alreadyReturned, 0);
      return {
        lineItemId: line.id,
        description: line.description,
        maxQty,
        unitPrice: Number(line.unitPrice),
        selected: false,
        quantity: maxQty > 0 ? 1 : 0,
        returnToStock: true,
        condition: 'good' as const,
        conditionNotes: '',
      };
    })
    .filter((d) => d.maxQty > 0);
}

export function ProcessReturnModal({
  open,
  sale,
  onClose,
  onSuccess,
}: {
  open: boolean;
  sale: SaleDetail | null;
  onClose: () => void;
  onSuccess: (result: Awaited<ReturnType<typeof processReturn>>) => void;
}) {
  const { requireOverride, modalProps } = useManagerOverride();
  const [step, setStep] = useState(0);
  const [drafts, setDrafts] = useState<ReturnItemDraft[]>([]);
  const [conditionIndex, setConditionIndex] = useState(0);
  const [refundMethod, setRefundMethod] = useState<RefundMethod>('cash');
  const [transactionId, setTransactionId] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!open || !sale) return;
    setStep(0);
    setDrafts(buildDrafts(sale.lineItems ?? []));
    setConditionIndex(0);
    const primaryMethod = sale.payments?.find((p) => p.status !== 'voided')?.method;
    setRefundMethod(
      primaryMethod === 'card' || primaryMethod === 'store_credit' ? primaryMethod : 'cash',
    );
    setTransactionId('');
    setNotes('');
  }, [open, sale]);

  const selected = useMemo(() => drafts.filter((d) => d.selected && d.quantity > 0), [drafts]);
  const refundAmount = useMemo(
    () => selected.reduce((sum, d) => sum + d.unitPrice * d.quantity, 0),
    [selected],
  );
  const originalPayment = sale?.payments?.find((p) => p.status !== 'voided');
  const saleNumber = sale?.saleNumber ?? sale?.ticketNumber ?? '';

  const mutation = useMutation({
    mutationFn: (body: ProcessReturnInput) => processReturn(sale!.id, body),
    onSuccess: (result) => {
      toastSuccess(
        `Return ${result.returnNumber} processed ✓ · ${formatCurrency(result.refundAmount)} refunded · ${result.itemsRestocked} item${result.itemsRestocked === 1 ? '' : 's'} restocked`,
      );
      onSuccess(result);
      onClose();
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  if (!open || !sale) return null;

  function updateDraft(lineItemId: string, patch: Partial<ReturnItemDraft>) {
    setDrafts((prev) => prev.map((d) => (d.lineItemId === lineItemId ? { ...d, ...patch } : d)));
  }

  function goNextFromSelect() {
    if (selected.length === 0) {
      toastError('Select at least one item to return');
      return;
    }
    setConditionIndex(0);
    setStep(1);
  }

  function goNextFromCondition() {
    if (conditionIndex < selected.length - 1) {
      setConditionIndex((i) => i + 1);
      return;
    }
    setStep(2);
  }

  function goBackFromCondition() {
    if (conditionIndex > 0) {
      setConditionIndex((i) => i - 1);
      return;
    }
    setStep(0);
  }

  async function handleConfirm() {
    if (refundMethod === 'card' && !transactionId.trim()) {
      toastError('Enter a refund transaction ID for card refunds');
      return;
    }
    const override = await requireOverride({
      action: 'sale.return',
      title: 'Manager Approval to Process Return',
      description: `Returning ${formatCurrency(refundAmount)} on ${saleNumber}`,
      context: {
        ticketId: sale.id,
        amount: refundAmount,
      },
    });
    if (!override.approved) return;

    mutation.mutate({
      items: selected.map((d) => ({
        lineItemId: d.lineItemId,
        quantity: d.quantity,
        returnToStock: d.condition === 'good' ? d.returnToStock : false,
        condition: d.condition,
        ...(d.conditionNotes.trim() ? { conditionNotes: d.conditionNotes.trim() } : {}),
      })),
      refundMethod,
      refundAmount: Math.round(refundAmount * 100) / 100,
      ...(transactionId.trim() ? { transactionId: transactionId.trim() } : {}),
      ...(notes.trim() ? { notes: notes.trim() } : {}),
      managerOverrideAuthorizedBy: override.authorizedBy.id,
    });
  }

  const conditionItem = selected[conditionIndex];
  const titles = ['Process Return', 'Item Condition', 'Refund Method', 'Confirm Return'];

  return (
    <>
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
        <button type="button" aria-label="Close" className="absolute inset-0 bg-slate-950/40" onClick={onClose} />
        <div className="relative flex max-h-[90vh] w-full max-w-lg flex-col rounded-2xl bg-white shadow-2xl">
          <header className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-950">{titles[step]}</h2>
              {step === 0 ? (
                <p className="text-sm text-slate-500">{saleNumber}</p>
              ) : null}
            </div>
            <button type="button" className="rounded-lg p-2 text-slate-500 hover:bg-slate-100" onClick={onClose}>
              <X className="h-5 w-5" />
            </button>
          </header>

          <div className="flex-1 overflow-y-auto p-5">
            {step === 0 ? (
              <div className="space-y-3">
                <p className="text-sm text-slate-600">Select items being returned:</p>
                {drafts.length === 0 ? (
                  <p className="rounded-xl bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
                    No returnable items on this sale.
                  </p>
                ) : (
                  drafts.map((draft) => (
                    <label
                      key={draft.lineItemId}
                      className={cn(
                        'flex cursor-pointer gap-3 rounded-xl border p-3',
                        draft.selected ? 'border-primary bg-primary/5' : 'border-slate-200',
                      )}
                    >
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={draft.selected}
                        onChange={(e) =>
                          updateDraft(draft.lineItemId, {
                            selected: e.target.checked,
                            quantity: e.target.checked ? Math.min(Math.max(draft.quantity, 1), draft.maxQty) : 0,
                          })
                        }
                      />
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-slate-950">{draft.description}</p>
                        <p className="mt-1 text-xs text-slate-500">
                          Qty sold: {draft.maxQty} · Unit price: {formatCurrency(draft.unitPrice)}
                        </p>
                        {draft.selected ? (
                          <div className="mt-2 flex items-center gap-2">
                            <span className="text-xs font-semibold text-slate-600">Returning:</span>
                            <input
                              type="number"
                              min={1}
                              max={draft.maxQty}
                              className="w-20 rounded-lg border border-slate-300 px-2 py-1 text-sm"
                              value={draft.quantity}
                              onChange={(e) =>
                                updateDraft(draft.lineItemId, {
                                  quantity: Math.min(
                                    Math.max(Number(e.target.value) || 1, 1),
                                    draft.maxQty,
                                  ),
                                })
                              }
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        ) : null}
                      </div>
                    </label>
                  ))
                )}
                <div className="border-t border-slate-200 pt-3 text-right text-sm font-bold text-slate-950">
                  Return Total: {formatCurrency(refundAmount)}
                </div>
              </div>
            ) : null}

            {step === 1 && conditionItem ? (
              <div className="space-y-4">
                <p className="text-base font-semibold text-slate-950">{conditionItem.description}</p>
                <p className="text-sm text-slate-600">Can this item be returned to stock?</p>
                <label className="flex cursor-pointer gap-3 rounded-xl border border-slate-200 p-3 hover:bg-slate-50">
                  <input
                    type="radio"
                    className="mt-1"
                    checked={conditionItem.condition === 'good'}
                    onChange={() =>
                      updateDraft(conditionItem.lineItemId, {
                        condition: 'good',
                        returnToStock: true,
                      })
                    }
                  />
                  <span>
                    <span className="block text-sm font-semibold">Yes — item is in good condition</span>
                    <span className="text-xs text-slate-500">Will be added back to inventory</span>
                  </span>
                </label>
                <label className="flex cursor-pointer gap-3 rounded-xl border border-slate-200 p-3 hover:bg-slate-50">
                  <input
                    type="radio"
                    className="mt-1"
                    checked={conditionItem.condition === 'damaged'}
                    onChange={() =>
                      updateDraft(conditionItem.lineItemId, {
                        condition: 'damaged',
                        returnToStock: false,
                      })
                    }
                  />
                  <span>
                    <span className="block text-sm font-semibold">No — item is damaged or unsellable</span>
                    <span className="text-xs text-slate-500">Will NOT be added back to inventory</span>
                  </span>
                </label>
                <label className="block space-y-1.5">
                  <span className="text-sm font-semibold text-slate-700">Condition notes (optional)</span>
                  <textarea
                    className="min-h-20 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
                    value={conditionItem.conditionNotes}
                    onChange={(e) =>
                      updateDraft(conditionItem.lineItemId, { conditionNotes: e.target.value })
                    }
                  />
                </label>
                <p className="text-xs text-slate-500">
                  Item {conditionIndex + 1} of {selected.length}
                </p>
              </div>
            ) : null}

            {step === 2 ? (
              <div className="space-y-4">
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm">
                  <p className="font-semibold text-slate-950">
                    Returning: {selected.map((s) => s.description).join(', ')}
                  </p>
                  <p className="mt-1">Refund Amount: {formatCurrency(refundAmount)}</p>
                  {originalPayment ? (
                    <p className="mt-1 text-slate-500">
                      Original payment: {paymentMethodEmoji(originalPayment.method)}{' '}
                      {paymentMethodLabel(originalPayment.method)}
                    </p>
                  ) : null}
                </div>

                <p className="text-sm font-semibold text-slate-800">How to refund:</p>
                {(
                  [
                    { value: 'cash' as const, label: `💵 Cash (${formatCurrency(refundAmount)})` },
                    { value: 'card' as const, label: '💳 Card (refund to original card)' },
                    {
                      value: 'store_credit' as const,
                      label: `🎁 Store Credit (${formatCurrency(refundAmount)})`,
                    },
                  ] as const
                ).map((opt) => (
                  <label
                    key={opt.value}
                    className="flex cursor-pointer gap-3 rounded-xl border border-slate-200 p-3 hover:bg-slate-50"
                  >
                    <input
                      type="radio"
                      className="mt-1"
                      checked={refundMethod === opt.value}
                      onChange={() => setRefundMethod(opt.value)}
                    />
                    <span className="text-sm font-semibold text-slate-950">{opt.label}</span>
                  </label>
                ))}

                {refundMethod === 'card' ? (
                  <label className="block space-y-1.5">
                    <span className="text-sm font-semibold text-slate-700">Transaction ID</span>
                    <input
                      className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
                      value={transactionId}
                      onChange={(e) => setTransactionId(e.target.value)}
                      placeholder="Refund transaction ID"
                    />
                  </label>
                ) : null}

                <label className="block space-y-1.5">
                  <span className="text-sm font-semibold text-slate-700">Notes (optional)</span>
                  <textarea
                    className="min-h-20 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </label>
              </div>
            ) : null}

            {step === 3 ? (
              <div className="space-y-4 text-sm">
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Return Summary</p>
                  <p className="mt-2 font-semibold text-slate-950">Original Sale: {saleNumber}</p>
                  <p className="text-slate-600">Customer: {sale.customerName ?? 'Walk-in'}</p>
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Items Returning</p>
                  <ul className="mt-2 space-y-1">
                    {selected.map((item) => (
                      <li key={item.lineItemId} className="flex justify-between gap-2">
                        <span>
                          ✓ {item.description}
                          {item.quantity > 1 ? ` ×${item.quantity}` : ''}
                        </span>
                        <span className="text-slate-500">
                          {item.condition === 'good' ? '→ Back to stock' : '→ Not restocked'}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Refund</p>
                  <p className="mt-1 font-semibold">
                    {formatCurrency(refundAmount)} {paymentMethodLabel(refundMethod).toLowerCase()} to customer
                  </p>
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Inventory</p>
                  <ul className="mt-1 space-y-1 text-slate-700">
                    {selected.map((item) => (
                      <li key={`inv-${item.lineItemId}`}>
                        {item.condition === 'good'
                          ? `+${item.quantity} ${item.description}`
                          : `No restock — ${item.description} (${item.condition})`}
                      </li>
                    ))}
                  </ul>
                </div>
                <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900">
                  Manager approval required.
                </p>
              </div>
            ) : null}
          </div>

          <footer className="flex items-center justify-between gap-2 border-t border-slate-200 px-5 py-4">
            <button
              type="button"
              className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              onClick={() => {
                if (step === 0) onClose();
                else if (step === 1) goBackFromCondition();
                else setStep((s) => s - 1);
              }}
            >
              {step === 0 ? 'Cancel' : 'Back'}
            </button>
            {step === 0 ? (
              <button
                type="button"
                className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-[#164477]"
                onClick={goNextFromSelect}
                disabled={drafts.length === 0}
              >
                Next: Condition →
              </button>
            ) : null}
            {step === 1 ? (
              <button
                type="button"
                className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-[#164477]"
                onClick={goNextFromCondition}
              >
                Next →
              </button>
            ) : null}
            {step === 2 ? (
              <button
                type="button"
                className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-[#164477]"
                onClick={() => setStep(3)}
              >
                Next: Review →
              </button>
            ) : null}
            {step === 3 ? (
              <button
                type="button"
                className="rounded-xl bg-accent px-4 py-2 text-sm font-semibold text-white hover:bg-accent/90 disabled:opacity-50"
                disabled={mutation.isPending}
                onClick={() => void handleConfirm()}
              >
                {mutation.isPending ? 'Processing…' : 'Process Return'}
              </button>
            ) : null}
          </footer>
        </div>
      </div>
      {modalProps ? <ManagerOverrideModal {...modalProps} /> : null}
    </>
  );
}
